import { useCallback, useState } from 'react';
import type {
  SoTienGuiNganHangItem,
  SoTienGuiNganHangRequest,
  SoTienGuiNganHangResponse
} from '@/types/schemas/so-tien-gui-ngan-hang.type';
import { useAuth } from '@/contexts/auth-context';
import { QUERY_KEYS } from '@/constants';
import api from '@/lib/api';

export interface UseSoTienGuiNganHangReturn {
  data: SoTienGuiNganHangItem[];
  isLoading: boolean;
  error: Error | null;
  fetchData: (searchParams: SoTienGuiNganHangRequest) => Promise<void>;
  refreshData: () => Promise<void>;
  submitSoTienGuiNganHang: (requestBody: SoTienGuiNganHangRequest) => Promise<void>;
}

export function useSoTienGuiNganHang(searchParams: SoTienGuiNganHangRequest): UseSoTienGuiNganHangReturn {
  const { entity } = useAuth();
  const [data, setData] = useState<SoTienGuiNganHangItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(
    async (searchParams: SoTienGuiNganHangRequest) => {
      if (!entity?.slug) {
        setError(new Error('Entity not found'));
        return;
      }

      if (!searchParams || Object.keys(searchParams).length === 0) {
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const response = await api.post<SoTienGuiNganHangResponse>(
          `/entities/${entity.slug}/erp/${QUERY_KEYS.SO_TIEN_GUI_NGAN_HANG}/`,
          searchParams
        );

        if (response.data && response.data.results && Array.isArray(response.data.results)) {
          setData(response.data.results);
        } else {
          setData([]);
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred while fetching data';
        setError(new Error(errorMessage));
        setData([]);
      } finally {
        setIsLoading(false);
      }
    },
    [entity?.slug]
  );

  const refreshData = useCallback(async () => {
    await fetchData(searchParams);
  }, [fetchData, searchParams]);

  const submitSoTienGuiNganHang = useCallback(
    async (requestBody: SoTienGuiNganHangRequest) => {
      if (!entity?.slug) {
        const error = new Error('Entity not found');
        throw error;
      }

      setIsLoading(true);
      setError(null);

      try {
        const response = await api.post<SoTienGuiNganHangResponse>(
          `/entities/${entity.slug}/erp/${QUERY_KEYS.SO_TIEN_GUI_NGAN_HANG}/`,
          requestBody
        );

        if (response.data && response.data.results && Array.isArray(response.data.results)) {
          setData(response.data.results);
        } else {
          setData([]);
        }
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred while submitting data';
        setError(new Error(errorMessage));
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [entity?.slug]
  );

  return {
    data,
    isLoading,
    error,
    fetchData,
    refreshData,
    submitSoTienGuiNganHang
  };
}

export const fetchAllSoTienGuiNganHang = async (
  entitySlug: string,
  body: SoTienGuiNganHangRequest,
  pageSize = 200
): Promise<SoTienGuiNganHangItem[]> => {
  let page = 1;
  let all: SoTienGuiNganHangItem[] = [];
  let totalCount = 0;

  while (true) {
    const response = await api.post<SoTienGuiNganHangResponse>(
      `/entities/${entitySlug}/erp/${QUERY_KEYS.SO_TIEN_GUI_NGAN_HANG}/`,
      body,
      {
        params: {
          page,
          page_size: pageSize
        }
      }
    );

    const pageResults = response.data.results || [];
    totalCount = response.data.count ?? totalCount;
    all = all.concat(pageResults);

    if (!pageResults.length) {
      break;
    }

    const hasNext = response.data.next;

    if (!hasNext && pageResults.length < pageSize) {
      break;
    }

    if (totalCount && all.length >= totalCount) {
      break;
    }

    page += 1;
  }

  return all;
};
